import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getAllPatients, Patient, checkQuestionnaireStatus, sendQuestionnaireEmail, QuestionnaireEmailData, generateSecureQuestionnaireLink, getQuestionnaireStatsByCategory, QuestionnaireStatsByCategory } from '../../api/admin';
import { AVAILABLE_QUESTIONNAIRES } from '../form/FormOptions';
import { EmailConfirmationModal } from './EmailConfirmationModal';
import './QuestionnaireAssignmentModal.css';

interface QuestionnaireAssignmentModalProps {
  show: boolean;
  onHide: () => void;
  preselectedPatientId?: string;
}

export function QuestionnaireAssignmentModal({ show, onHide, preselectedPatientId }: QuestionnaireAssignmentModalProps) {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState<string[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<string>('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [sortedPatients, setSortedPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [generatedLinks, setGeneratedLinks] = useState<Array<{questionnaire: string, name: string, link: string, entryCode: string}>>([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  
  // Email functionality states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState<string>('');
  
  // Questionnaire status tracking
  const [questionnaireStatuses, setQuestionnaireStatuses] = useState<Record<string, {
    isCompleted: boolean;
    status: string;
    completedAt?: string;
    createdAt?: string;
    scores?: any;
    completedCount?: number; // Number of times this questionnaire type has been completed
    inProgressCount?: number; // Number of in-progress questionnaires of this type
  }>>({});

  // Questionnaire statistics by category
  const [questionnaireStats, setQuestionnaireStats] = useState<QuestionnaireStatsByCategory>({});

  // Load patients when modal opens
  useEffect(() => {
    if (show) {
      loadPatients();
    }
  }, [show]);

  // Sort patients when patients change
  useEffect(() => {
    const sorted = [...patients].sort((a, b) => {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
    setSortedPatients(sorted);
  }, [patients]);

  // Auto-select preselected patient when modal opens and patients are loaded
  useEffect(() => {
    if (show && preselectedPatientId && patients.length > 0) {
      const patientExists = patients.find(p => p.patient_id === preselectedPatientId);
      if (patientExists) {
        setSelectedPatient(preselectedPatientId);
      }
    }
  }, [show, preselectedPatientId, patients]);

  // Check questionnaire statuses when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      checkAllQuestionnaireStatuses();
      loadQuestionnaireStats();
    } else {
      setQuestionnaireStatuses({});
      setQuestionnaireStats({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient]);

  // Handle Enter key to trigger "Generate Patient Link"
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only trigger if modal is shown and Enter key is pressed
      if (show && event.key === 'Enter') {
        // Check if the button would be enabled (same conditions as the button)
        const isButtonEnabled = selectedQuestionnaires.length > 0 && selectedPatient && !loading;
        
        if (isButtonEnabled) {
          event.preventDefault();
          handleAssignment();
        }
      }
    };

    if (show) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedQuestionnaires.length, selectedPatient, loading]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to access this feature');
        return;
      }
      
      const response = await getAllPatients();
      setPatients(response || []);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        // Optionally redirect to login
        // window.location.href = '/login';
      } else {
        setError('Failed to load patients: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const checkAllQuestionnaireStatuses = async () => {
    if (!selectedPatient) return;

    try {
      const statuses: Record<string, any> = {};
      
      // Check status for all available questionnaires
      for (const questionnaire of AVAILABLE_QUESTIONNAIRES) {
        const status = await checkQuestionnaireStatus(selectedPatient, questionnaire.id);
        statuses[questionnaire.id] = status;
      }
      
      setQuestionnaireStatuses(statuses);
    } catch (err: any) {
      console.error('Failed to check questionnaire statuses:', err);
      // Don't show error to user, just log it
    }
  };

  const loadQuestionnaireStats = async () => {
    if (!selectedPatient) return;

    try {
      const stats = await getQuestionnaireStatsByCategory(selectedPatient);
      setQuestionnaireStats(stats);
    } catch (err: any) {
      console.error('Failed to load questionnaire stats:', err);
      // Don't show error to user, just log it
    }
  };

  const navigateToAnalytics = (questionnaireId: string, status: 'completed' | 'in_progress') => {
    // Close the modal first
    handleClose();
    
    // Navigate to analytics page with filters
    // We'll use URL search params to pass the filters
    const searchParams = new URLSearchParams();
    searchParams.set('patient', selectedPatient);
    searchParams.set('questionnaire', questionnaireId);
    searchParams.set('status', status);
    
    navigate(`/admin/analytics?${searchParams.toString()}`);
  };

  const handleQuestionnaireToggle = (questionnaireId: string) => {
    // Clear any previous errors when user makes selections
    setError('');
    
    setSelectedQuestionnaires(prev => {
      if (prev.includes(questionnaireId)) {
        // Remove if already selected
        return prev.filter(id => id !== questionnaireId);
      } else {
        // Add if not selected
        return [...prev, questionnaireId];
      }
    });
  };

  const toggleDescription = (questionnaireId: string, event: React.MouseEvent) => {
    // Prevent the card click from triggering when clicking the description toggle
    event.stopPropagation();
    
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionnaireId)) {
        newSet.delete(questionnaireId);
      } else {
        newSet.add(questionnaireId);
      }
      return newSet;
    });
  };

  const handleAssignment = async () => {
    // Clear any previous errors
    setError('');
    
    if (selectedQuestionnaires.length === 0) {
      setError('Please select at least one questionnaire');
      return;
    }
    
    if (!selectedPatient) {
      setError('Please select a patient');
      return;
    }

    // Find selected patient
    const patient = patients.find(p => p.patient_id === selectedPatient);
    
    if (!patient) {
      setError('Invalid patient selection');
      return;
    }

    // Show email modal instead of generating links immediately (NEW ARCHITECTURE)
    setShowEmailModal(true);
  };


  const handleClose = () => {
    setSelectedQuestionnaires([]);
    setSelectedPatient('');
    setExpandedDescriptions(new Set());
    setError('');
    setSuccess('');
    setGeneratedLinks([]);
    setShowEmailModal(false);
    setEmailSuccess('');
    setQuestionnaireStatuses({});
    setQuestionnaireStats({});
    onHide();
  };

  const copyLinkToClipboard = (link: string) => {
    navigator.clipboard.writeText(link);
    // Could add a toast notification here
  };

  const handleSendEmail = async (email: string, language: string, generatedLinks?: Array<{link: string}>) => {
    if (!selectedPatient) {
      return;
    }

    try {
      setEmailLoading(true);
      
      // Find selected patient
      const patient = patients.find(p => p.patient_id === selectedPatient);
      if (!patient) {
        throw new Error('Patient not found');
      }

      // NEW ARCHITECTURE: Generate links now if not provided (links only, no OTP)
      let linksToUse = generatedLinks;
      if (!linksToUse || linksToUse.length === 0) {
        // Generate secure links for all selected questionnaires (link only, OTP generated when patient clicks)
        const linkPromises = selectedQuestionnaires.map(async (questionnaireId) => {
          const secureLink = await generateSecureQuestionnaireLink(questionnaireId, patient.patient_id);
          
          return {
            questionnaire: questionnaireId,
            name: secureLink.name,  // Use the name from the backend response
            link: secureLink.link,
            questionnaire_id: secureLink.questionnaire_id  // Include the questionnaire_id
            // Note: No OTP code - it will be generated when patient clicks the link
          };
        });

        const links = await Promise.all(linkPromises);
        setGeneratedLinks(links.map(link => ({...link, entryCode: 'Generated on access'})));
        linksToUse = links;
      }

      // Convert generated links to email format (no OTP codes)
      const emailQuestionnaires: QuestionnaireEmailData[] = linksToUse.map((link: any) => ({
        questionnaire_id: link.questionnaire_id || link.questionnaire,
        name: link.name,
        link: link.link
      }));

      // Send email
      const result = await sendQuestionnaireEmail({
        patient_id: selectedPatient,
        patient_email: email,
        questionnaires: emailQuestionnaires,
        language: language
      });

      if (result.success) {
        setEmailSuccess(`Email sent successfully to ${result.sent_to}! ${result.questionnaires_sent} questionnaire(s) included.`);
        setShowEmailModal(false);
        
        // Show success in main modal
        const questionnaireNames = selectedQuestionnaires.map(id => 
          AVAILABLE_QUESTIONNAIRES.find(q => q.id === id)?.name || id
        ).join(', ');
        setSuccess(`Secure questionnaire links generated and email sent for "${questionnaireNames}" to ${patient.first_name} ${patient.last_name}`);
      } else {
        throw new Error(result.message || 'Failed to send email');
      }
    } catch (err: any) {
      setError('Failed to send email: ' + (err.message || 'Unknown error'));
      setShowEmailModal(false); // Close modal so user can see error in main modal
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-clipboard-check me-2"></i>
          Assign Questionnaire to Patient
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body ref={modalBodyRef}>
        {error && <Alert variant="danger">{error}</Alert>}
        {emailSuccess && (
          <Alert variant="success" className="email-success-alert">
            <div className="alert-content">
              <i className="bi bi-check-circle-fill success-icon"></i>
              <span>{emailSuccess}</span>
            </div>
          </Alert>
        )}
        {success && (
          <Alert variant="success">
            <div>{success}</div>
            {generatedLinks.length > 0 && (
              <div className="mt-3">
                <strong>🔒 Secure Patient {generatedLinks.length === 1 ? 'Link' : 'Links'}:</strong>
                
                {/* Single instruction message at the top */}
                <div className="alert alert-info py-2 px-3 mt-2 mb-3">
                  <small>
                    <strong>Instructions:</strong> Share both the {generatedLinks.length === 1 ? 'link' : 'links'} and OTP {generatedLinks.length === 1 ? 'code' : 'codes'} with the patient. 
                    The {generatedLinks.length === 1 ? 'link is' : 'links are'} valid for 24 hours, but the OTP {generatedLinks.length === 1 ? 'code expires' : 'codes expire'} in 2 minutes after being generated.
                  </small>
                </div>

                {generatedLinks.map((linkData, index) => (
                  <div key={linkData.questionnaire} className="mt-3 p-3 border rounded bg-light">
                    <div className="mb-2">
                      <label className="form-label small fw-bold mb-0">{linkData.name}</label>
                    </div>
                    
                    <div className="mb-2">
                      <label className="form-label small text-muted">Questionnaire Link:</label>
                      <div className="input-group">
                        <input 
                          type="text" 
                          className="form-control font-monospace" 
                          value={linkData.link} 
                          readOnly 
                        />
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => copyLinkToClipboard(linkData.link)}
                          title={`Copy ${linkData.name} link`}
                        >
                          <i className="bi bi-clipboard"></i>
                        </Button>
                      </div>
                    </div>
                    
                    <div className="mb-0">
                      <label className="form-label small text-muted">6-Digit OTP Code (2-minute expiration):</label>
                      <div className="input-group">
                        <input 
                          type="text" 
                          className="form-control font-monospace fw-bold text-center bg-warning bg-opacity-25" 
                          value={linkData.entryCode} 
                          readOnly 
                        />
                        <Button 
                          variant="outline-warning" 
                          onClick={() => copyLinkToClipboard(linkData.entryCode)}
                          title={`Copy ${linkData.name} OTP code`}
                        >
                          <i className="bi bi-key"></i>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Email Action Button */}
                {generatedLinks.length > 0 && (
                  <div className="mt-4 text-center">
                    <Button 
                      variant="success" 
                      size="lg"
                      onClick={() => setShowEmailModal(true)}
                      className="px-4"
                    >
                      <i className="bi bi-envelope me-2"></i>
                      Send Email to Patient
                    </Button>
                    <div className="small text-muted mt-2">
                      Send an automated email with all questionnaire links and OTP codes
                    </div>
                  </div>
                )}
              </div>
            )}
          </Alert>
        )}

        <Row>
          <Col md={6}>
            <h5 className="mb-2">
              <i className="bi bi-person me-2"></i>
              Select Patient
              <span className="text-danger ms-1">*</span>
            </h5>
            
            {/* Patient Selection Dropdown */}
            <Form.Group className="mb-2">
              <Form.Select 
                value={selectedPatient} 
                onChange={(e) => {
                  setSelectedPatient(e.target.value);
                  setSelectedQuestionnaires([]); // Clear questionnaire selections when patient changes
                  setExpandedDescriptions(new Set()); // Clear expanded descriptions
                  setError(''); // Clear errors when patient selection changes
                }}
                disabled={loading}
                className={`patient-select ${selectedPatient ? 'border-success' : ''}`}
              >
                <option value="">Choose a patient...</option>
                {sortedPatients.map((patient) => {
                  // Create mobile-friendly display name
                  const fullName = `${patient.first_name} ${patient.last_name}`;
                  const dateOfBirth = patient.date_of_birth ? 
                    new Date(patient.date_of_birth).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    }) : '';
                  
                  // Create different displays for different screen sizes
                  const fullDisplay = dateOfBirth ? `${fullName} (DOB: ${dateOfBirth})` : fullName;
                  
                  // Mobile-optimized display - shorter format
                  const mobileDisplay = dateOfBirth ? 
                    `${patient.first_name} ${patient.last_name.charAt(0)}. (${dateOfBirth.replace(',', '').replace(' ', '/')})` : 
                    fullName;
                  
                  // Use data attribute for responsive display
                  return (
                    <option 
                      key={patient.patient_id} 
                      value={patient.patient_id}
                      title={fullDisplay} // Show full info on hover/touch
                      data-full-name={fullDisplay}
                      data-mobile-name={mobileDisplay}
                      className="patient-option"
                    >
                      {fullDisplay}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>

            {loading && (
              <div className="text-center mt-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="small text-muted mt-2">Loading patients...</div>
              </div>
            )}
          </Col>

          <Col md={6}>
            <h5 className="mb-2">
              <i className="bi bi-clipboard-data me-2"></i>
              Select Questionnaire{selectedQuestionnaires.length > 1 ? 's' : ''}
            </h5>
            <div className="questionnaire-selection">
              {AVAILABLE_QUESTIONNAIRES.map((questionnaire) => (
                <Card 
                  key={questionnaire.id} 
                  className={`mb-3 questionnaire-card ${
                    selectedQuestionnaires.includes(questionnaire.id) ? 'border-primary selected' : ''
                  } ${!selectedPatient ? 'disabled' : ''}`}
                  style={{ 
                    cursor: selectedPatient ? 'pointer' : 'not-allowed',
                    opacity: selectedPatient ? 1 : 0.6
                  }}
                  onClick={() => selectedPatient && handleQuestionnaireToggle(questionnaire.id)}
                >
                  <Card.Body>
                    <div className="d-flex align-items-start">
                      <Form.Check
                        type="checkbox"
                        name={`questionnaire-${questionnaire.id}`}
                        checked={selectedQuestionnaires.includes(questionnaire.id)}
                        onChange={() => selectedPatient && handleQuestionnaireToggle(questionnaire.id)}
                        disabled={!selectedPatient}
                        className="me-3"
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <Card.Title className="h6 mb-0" style={{ fontSize: '0.95rem' }}>{questionnaire.name}</Card.Title>
                          {selectedPatient && questionnaireStatuses[questionnaire.id] && (
                            <div className="ms-2">
                              {questionnaireStatuses[questionnaire.id].isCompleted ? (
                                <span className="badge bg-success">
                                  <i className="bi bi-check-circle me-1"></i>
                                  Completed
                                </span>
                              ) : questionnaireStatuses[questionnaire.id].status === 'in_progress' ? (
                                <span className="badge bg-warning">
                                  <i className="bi bi-clock me-1"></i>
                                  In Progress
                                </span>
                              ) : (
                                <span className="badge bg-secondary">
                                  <i className="bi bi-circle me-1"></i>
                                  Not Started
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Mobile description toggle button */}
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <div className="small">
                            <span className="badge bg-light text-dark me-1">
                              <i className="bi bi-clock me-1"></i>
                              {questionnaire.estimatedTime}
                            </span>
                            <span className="badge bg-light text-dark">
                              <i className="bi bi-question-circle me-1"></i>
                              {questionnaire.questions} questions
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 d-md-none description-toggle"
                            onClick={(e) => selectedPatient && toggleDescription(questionnaire.id, e)}
                            disabled={!selectedPatient}
                            aria-label={expandedDescriptions.has(questionnaire.id) ? "Hide description" : "Show description"}
                          >
                            <i className={`bi ${expandedDescriptions.has(questionnaire.id) ? 'bi-chevron-up' : 'bi-info-circle'}`}></i>
                          </button>
                        </div>
                        
                        {/* Description - always visible on desktop, collapsible on mobile */}
                        <Card.Text 
                          className={`small text-muted mb-1 questionnaire-description ${
                            expandedDescriptions.has(questionnaire.id) ? 'expanded' : ''
                          }`}
                          style={{ fontSize: '0.8rem', lineHeight: '1.3' }}
                        >
                          {questionnaire.description.en}
                        </Card.Text>
                        
                        {/* Progress buttons section */}
                        {selectedPatient && questionnaireStats[questionnaire.id] && (
                          <div className="progress-buttons-container">
                            {/* Completed questionnaires count button */}
                            {questionnaireStats[questionnaire.id].completedCount > 0 && (
                              <button
                                type="button"
                                className="btn btn-success progress-button"
                                title={`Click to view ${questionnaireStats[questionnaire.id].completedCount} completed ${questionnaire.name} questionnaire(s) in analytics`}
                                onClick={() => navigateToAnalytics(questionnaire.id, 'completed')}
                              >
                                <i className="bi bi-check-circle"></i>
                                <span>{questionnaireStats[questionnaire.id].completedCount}/∞</span>
                                <small>
                                  ({questionnaireStats[questionnaire.id].lastCompletedAt ? 
                                    new Date(questionnaireStats[questionnaire.id].lastCompletedAt!).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric'
                                    }) : 'N/A'})
                                </small>
                              </button>
                            )}

                            {/* In Progress button */}
                            {questionnaireStats[questionnaire.id].inProgressCount > 0 && (
                              <button
                                type="button"
                                className="btn btn-warning progress-button"
                                title={`Click to view ${questionnaire.name} questionnaire(s) in progress in analytics`}
                                onClick={() => navigateToAnalytics(questionnaire.id, 'in_progress')}
                              >
                                <i className="bi bi-clock"></i>
                                <span>In Progress ({questionnaireStats[questionnaire.id].inProgressCount})</span>
                                <small>
                                  ({questionnaireStatuses[questionnaire.id]?.createdAt ? 
                                    new Date(questionnaireStatuses[questionnaire.id].createdAt!).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric'
                                    }) : 'N/A'})
                                </small>
                              </button>
                            )}

                            {/* Not started - show empty progress */}
                            {questionnaireStats[questionnaire.id].completedCount === 0 && 
                             questionnaireStats[questionnaire.id].inProgressCount === 0 && (
                              <button
                                type="button"
                                className="btn btn-outline-secondary progress-button"
                                disabled
                              >
                                <i className="bi bi-circle"></i>
                                <span>0/∞</span>
                              </button>
                            )}
                          </div>
                        )}

                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Col>
        </Row>
      </Modal.Body>

      {/* Warning for completed questionnaires */}
      {selectedPatient && selectedQuestionnaires.length > 0 && (() => {
        const completedSelected = selectedQuestionnaires.filter(id => 
          questionnaireStatuses[id]?.isCompleted
        );
        
        if (completedSelected.length > 0) {
          return (
            <div className="px-3 pb-2">
              <Alert variant="info" className="mb-0">
                <Alert.Heading className="h6">
                  <i className="bi bi-info-circle me-2"></i>
                  Information: Completed Questionnaires Selected
                </Alert.Heading>
                <p className="mb-2">
                  {completedSelected.length} of the selected questionnaire{completedSelected.length > 1 ? 's have' : ' has'} already been completed by this patient.
                </p>
                <p className="mb-0 small">
                  <strong>Note:</strong> If you proceed, the patient will be able to complete the questionnaire again, 
                  and the new responses will be added as a separate completion alongside the existing ones.
                </p>
              </Alert>
            </div>
          );
        }
        return null;
      })()}

      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleClose} 
          style={{ 
            width: '150px', 
            height: '38px',
            padding: '6px 12px',
            fontSize: '14px'
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleAssignment}
          disabled={selectedQuestionnaires.length === 0 || !selectedPatient || loading}
          style={{ 
            width: '200px', 
            height: '38px',
            padding: '6px 12px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          Generate Patient Link{selectedQuestionnaires.length > 1 ? 's' : ''}
          {loading && (
            <span 
              className="spinner-border" 
              role="status" 
              aria-hidden="true"
              style={{
                width: '12px',
                height: '12px',
                borderWidth: '1px',
                marginLeft: '8px',
                flexShrink: 0
              }}
            ></span>
          )}
        </Button>
      </Modal.Footer>

      {/* Email Confirmation Modal */}
      <EmailConfirmationModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        onConfirm={handleSendEmail}
        patientName={patients.find(p => p.patient_id === selectedPatient) ? 
          `${patients.find(p => p.patient_id === selectedPatient)!.first_name} ${patients.find(p => p.patient_id === selectedPatient)!.last_name}` : 
          'Unknown Patient'
        }
        patientEmail={patients.find(p => p.patient_id === selectedPatient)?.email || ''}
        patientId={selectedPatient}
        questionnaires={selectedQuestionnaires.map(id => {
          const questionnaire = AVAILABLE_QUESTIONNAIRES.find(q => q.id === id);
          return {
            questionnaire: id,
            name: questionnaire?.name || id
          };
        })}
        loading={emailLoading}
      />
    </Modal>
  );
}
